// eslint-disable-next-line no-restricted-imports
import {CountryCode} from '@sail/ui';
import {
  GlobalizationPair,
  GlobalizationPathPrefix,
  GlobalizationPathPrefixData,
  LocaleCode,
  PrefixablePair,
} from 'mkt-ssr/lib/globalization/types';
import {PathPrefixes} from 'mkt-ssr/generated/globalization/pathPrefixes';
import {Countries} from 'mkt-ssr/generated/globalization/countries';

export const DEFAULT_PREFIX = PathPrefixes.us;

function formatCodesForLookup({
  country,
  locale,
}: {
  locale: LocaleCode;
  country: CountryCode;
}): string {
  return `${locale}-${country}`;
}

function formatPairForLookup({country, locale}: PrefixablePair): string {
  return formatCodesForLookup({country: country.code, locale: locale.code});
}

const PREFIXES_BY_PAIR: Record<string, GlobalizationPathPrefix> = Object.values(
  PathPrefixes,
).reduce(
  (prev, {prefix, ...pair}) => ({
    ...prev,
    [formatPairForLookup(pair)]: prefix,
  }),
  {},
);

export function isCountryCode(
  maybeCode: string,
): maybeCode is keyof typeof Countries {
  return maybeCode in Countries;
}

export function isGlobalizationPrefix(
  prefix: string,
): prefix is GlobalizationPathPrefix {
  return prefix.toLowerCase() in PathPrefixes;
}

export function getPrefixData(prefix: string): GlobalizationPathPrefixData {
  const normalizedPrefix = prefix.toLowerCase();
  if (isGlobalizationPrefix(normalizedPrefix)) {
    return PathPrefixes[normalizedPrefix];
  } else {
    return DEFAULT_PREFIX;
  }
}

export function pairForCode(codes: {
  locale: LocaleCode;
  country: CountryCode;
}): PrefixablePair {
  return getPrefixData(PREFIXES_BY_PAIR[formatCodesForLookup(codes)]);
}

export function getPrefixForPair(
  pair: PrefixablePair,
): GlobalizationPathPrefix | undefined {
  return PREFIXES_BY_PAIR[formatPairForLookup(pair)];
}

const COUNTRY_DISPLAY_OVERRIDES: Record<string, string> = {
  [Countries.HK.code]: 'HK-alt-short',
} as const;

const LOCALE_DISPLAY_OVERRIDES: Record<string, string> = {};

export function getDisplayPair({country, locale}: GlobalizationPair) {
  return {
    country: COUNTRY_DISPLAY_OVERRIDES[country.code] || country.code,
    locale: LOCALE_DISPLAY_OVERRIDES[locale.code] || locale.code,
  };
}

export function stripGlobalizationPrefix(path: string, pair: PrefixablePair) {
  const prefix = getPrefixForPair(pair);
  if (prefix) {
    return path
      .replace(new RegExp(`^/${prefix}/`, 'i'), '/')
      .replace(new RegExp(`^/${prefix}$`, 'i'), '/');
  } else {
    return path;
  }
}
